import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'

export const InViewAutoplayingYoutubeVideo = ({
  className,
  youtubeEmbedUrl,
  thumbnailUrl,
  title,
}: {
  className?: string
  youtubeEmbedUrl: string
  thumbnailUrl: string
  title: string
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const hasEnteredViewRef = useRef(false)
  const [isVideoVisible, setIsVideoVisible] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          hasEnteredViewRef.current = true
          setIsVideoVisible(true)
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: '' }),
            '*',
          )
        } else if (hasEnteredViewRef.current) {
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
            '*',
          )
        }
      },
      { threshold: 0.3 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div className={className}>
      <div
        ref={containerRef}
        style={{ aspectRatio: 16 / 9 }}
        className="relative mt-6 w-full overflow-hidden rounded-2xl"
      >
        <div
          className={classNames('absolute inset-0 transition-opacity duration-700', {
            'pointer-events-none opacity-0': isVideoVisible,
            'opacity-100': !isVideoVisible,
          })}
        >
          <Image
            src={thumbnailUrl}
            alt={title}
            fill
            sizes="(max-width: 1023px) 100vw, 856px"
            className="object-cover"
          />
        </div>

        <iframe
          ref={iframeRef}
          className={classNames('absolute inset-0 h-full w-full transition-opacity duration-700', {
            'opacity-100': isVideoVisible,
            'opacity-0': !isVideoVisible,
          })}
          src={youtubeEmbedUrl}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  )
}
